import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import { setStatusBarColor } from '../lib/statusBarColor';

/** The camera screen's background; the browser bars take it on too while it is open. */
const CAMERA_BACKGROUND = '#121614';

type Shot = { file: File; url: string };
type Facing = 'environment' | 'user';
/** Torch (flash) is in Chrome on Android; Safari does not report it. */
type TorchCapabilities = MediaTrackCapabilities & { torch?: boolean };

/** True where the browser can show the camera itself (a secure page with getUserMedia). */
export function inAppCameraSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof window !== 'undefined' &&
    window.isSecureContext
  );
}

/**
 * Taking photos inside the app on a phone: a viewfinder with shutter, flash and the other
 * camera, then a review of the shots with a question to send along. Photos stay in memory
 * until they are sent or added; nothing is stored on the device. Without camera access the
 * screen offers the phone's own camera app instead (`onFallback`).
 */
export function CameraSheet({
  onDone,
  onClose,
  onFallback,
}: {
  /** The photos, and the question to send with them (empty: only add them to the message). */
  onDone: (files: File[], question: string) => void;
  onClose: () => void;
  onFallback: () => void;
}) {
  const t = useT();
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [facing, setFacing] = useState<Facing>('environment');
  const [mode, setMode] = useState<'photo' | 'document'>('photo');
  const [failed, setFailed] = useState(false);
  const [torch, setTorch] = useState<{ available: boolean; on: boolean }>({
    available: false,
    on: false,
  });
  const [shots, setShots] = useState<Shot[]>([]);
  const [review, setReview] = useState<number | null>(null);
  const [question, setQuestion] = useState('');
  const shotsRef = useRef(shots);
  shotsRef.current = shots;

  // The status bar and the browser's bars turn dark with the camera, and back when it closes.
  useEffect(() => {
    const restore = setStatusBarColor(CAMERA_BACKGROUND);
    document.documentElement.classList.add('nv-camera-on');
    return () => {
      restore();
      document.documentElement.classList.remove('nv-camera-on');
      for (const shot of shotsRef.current) URL.revokeObjectURL(shot.url);
    };
  }, []);

  // The live picture; it stops while reviewing and whenever the screen closes.
  useEffect(() => {
    if (review !== null) return;
    let cancelled = false;
    setFailed(false);
    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1440 } },
        audio: false,
      })
      .then((media) => {
        if (cancelled) {
          for (const track of media.getTracks()) track.stop();
          return;
        }
        stream.current = media;
        if (video.current) {
          video.current.srcObject = media;
          void video.current.play().catch(() => undefined);
        }
        const capabilities = media.getVideoTracks()[0]?.getCapabilities?.() as
          | TorchCapabilities
          | undefined;
        setTorch({ available: capabilities?.torch === true, on: false });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      for (const track of stream.current?.getTracks() ?? []) track.stop();
      stream.current = null;
    };
  }, [facing, review]);

  async function toggleTorch() {
    const track = stream.current?.getVideoTracks()[0];
    if (!track) return;
    const on = !torch.on;
    try {
      await track.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] });
      setTorch({ available: true, on });
    } catch {
      setTorch({ available: false, on: false });
    }
  }

  function takePhoto() {
    const el = video.current;
    if (!el?.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = el.videoWidth;
    canvas.height = el.videoHeight;
    canvas.getContext('2d')?.drawImage(el, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
        const file = new File([blob], `foto-${stamp}-${shotsRef.current.length + 1}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        setShots((current) => [...current, { file, url: URL.createObjectURL(file) }]);
      },
      'image/jpeg',
      0.9,
    );
  }

  function removeShot(index: number) {
    const shot = shots[index];
    if (shot) URL.revokeObjectURL(shot.url);
    const next = shots.filter((_, i) => i !== index);
    setShots(next);
    setReview(next.length === 0 ? null : Math.min(index, next.length - 1));
  }

  const finish = (text: string) =>
    onDone(
      shots.map((shot) => shot.file),
      text.trim(),
    );
  const shown = review !== null ? shots[review] : undefined;

  return createPortal(
    <div className="nv-camera" role="dialog" aria-modal="true" aria-label={t('takePhoto')}>
      <header className="nv-camera-top">
        {shown ? (
          <>
            <button
              type="button"
              className="nv-camera-ib"
              aria-label={t('cameraBack')}
              onClick={() => setReview(null)}
            >
              <CameraIcon name="back" />
            </button>
            <span className="nv-camera-title">
              {t('cameraPhotoOf')
                .replace('{n}', String((review ?? 0) + 1))
                .replace('{total}', String(shots.length))}
            </span>
            <button
              type="button"
              className="nv-camera-ib"
              aria-label={t('cameraDelete')}
              onClick={() => removeShot(review ?? 0)}
            >
              <CameraIcon name="trash" />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="nv-camera-ib"
              aria-label={t('close')}
              onClick={onClose}
            >
              <CameraIcon name="close" />
            </button>
            <fieldset className="nv-camera-modes">
              <legend className="sr-only">{t('cameraMode')}</legend>
              {(['photo', 'document'] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  aria-pressed={mode === item}
                  onClick={() => setMode(item)}
                >
                  {t(item === 'photo' ? 'cameraModePhoto' : 'cameraModeDocument')}
                </button>
              ))}
            </fieldset>
            {torch.available ? (
              <button
                type="button"
                className="nv-camera-ib"
                aria-pressed={torch.on}
                aria-label={t(torch.on ? 'cameraFlashOff' : 'cameraFlashOn')}
                onClick={() => void toggleTorch()}
              >
                <CameraIcon name={torch.on ? 'flash' : 'flashOff'} />
              </button>
            ) : (
              <span className="nv-camera-ib-gap" />
            )}
          </>
        )}
      </header>

      <div className="nv-camera-view">
        {shown ? (
          <img src={shown.url} alt={t('cameraShot')} />
        ) : failed ? (
          <div className="nv-camera-failed" role="alert">
            <p>{t('cameraNoAccess')}</p>
            <button type="button" className="nv-camera-pill" onClick={onFallback}>
              {t('cameraUsePhoneApp')}
            </button>
          </div>
        ) : (
          <>
            <video ref={video} playsInline muted autoPlay aria-label={t('cameraLive')} />
            <div
              className={mode === 'document' ? 'nv-camera-frame nv-camera-doc' : 'nv-camera-frame'}
              aria-hidden="true"
            />
            <span className="nv-camera-note">{t('cameraPrivate')}</span>
          </>
        )}
      </div>

      {shown ? (
        <>
          <div className="nv-camera-strip">
            {shots.map((shot, index) => (
              <button
                key={shot.url}
                type="button"
                className="nv-camera-thumb"
                aria-current={index === review ? 'true' : undefined}
                aria-label={t('cameraPhotoOf')
                  .replace('{n}', String(index + 1))
                  .replace('{total}', String(shots.length))}
                onClick={() => setReview(index)}
              >
                <img src={shot.url} alt="" />
              </button>
            ))}
            <button
              type="button"
              className="nv-camera-thumb nv-camera-more"
              aria-label={t('cameraAnother')}
              onClick={() => setReview(null)}
            >
              <CameraIcon name="plus" />
            </button>
          </div>
          <form
            className="nv-camera-send"
            onSubmit={(event) => {
              event.preventDefault();
              finish(question);
            }}
          >
            <div className="nv-camera-ask">
              <label htmlFor="nv-camera-question" className="sr-only">
                {t('cameraQuestion')}
              </label>
              <input
                id="nv-camera-question"
                type="text"
                value={question}
                placeholder={t('cameraQuestion')}
                onChange={(event) => setQuestion(event.target.value)}
              />
              <button
                type="submit"
                // Keep the keyboard open until the tap lands, so one tap sends.
                onPointerDown={(event) => event.preventDefault()}
                onMouseDown={(event) => event.preventDefault()}
                className="nv-camera-go"
                aria-label={t('send')}
                disabled={!question.trim()}
              >
                <CameraIcon name="send" />
              </button>
            </div>
            <button type="button" className="nv-camera-link" onClick={() => finish('')}>
              {t('cameraOnlyAdd')}
            </button>
          </form>
        </>
      ) : (
        <div className="nv-camera-bottom">
          <button
            type="button"
            className="nv-camera-last"
            aria-label={t('cameraReview').replace('{count}', String(shots.length))}
            disabled={shots.length === 0}
            onClick={() => setReview(shots.length - 1)}
          >
            {shots.at(-1) && <img src={shots.at(-1)?.url} alt="" />}
            {shots.length > 0 && <span>{shots.length}</span>}
          </button>
          <button
            type="button"
            className="nv-camera-shutter"
            aria-label={t('cameraShutter')}
            disabled={failed}
            onClick={takePhoto}
          >
            <span />
          </button>
          <button
            type="button"
            className="nv-camera-ib nv-camera-flip"
            aria-label={t('cameraFlip')}
            disabled={failed}
            onClick={() =>
              setFacing((current) => (current === 'environment' ? 'user' : 'environment'))
            }
          >
            <CameraIcon name="flip" />
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}

const ICONS: Record<string, string[]> = {
  close: ['M6 6l12 12', 'M18 6L6 18'],
  back: ['M15 6l-6 6 6 6'],
  trash: ['M4 7h16', 'M9 7V4h6v3', 'M6 7l1 13h10l1-13'],
  plus: ['M12 5v14', 'M5 12h14'],
  send: ['M12 19V5', 'M6 11l6-6 6 6'],
  flash: ['M13 3L5 14h6l-1 7 8-11h-6z'],
  flashOff: ['M13 3L5 14h6l-1 7 8-11h-6z', 'M4 4l16 16'],
  flip: ['M4 12a8 8 0 0 1 14-5.3L20 9', 'M20 4v5h-5', 'M20 12a8 8 0 0 1-14 5.3L4 15', 'M4 20v-5h5'],
};

function CameraIcon({ name }: { name: keyof typeof ICONS }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICONS[name]?.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
